import { AlertCircle, Check, Pencil, SpellCheck, Trash2 } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../lib/utils.js';
import type { GrammarErrorView, GrammarEvaluationView } from '../utils.js';

const GRAMMAR_CATEGORIES = [
  'TYPOS',
  'GRAMMAR',
  'SEMANTICS',
  'STYLE',
  'PUNCTUATION',
  'TYPOGRAPHY',
  'REDUNDANCY',
  'CASING',
  'COLLOCATIONS',
  'CONFUSED_WORDS',
  'FALSE_FRIENDS',
  'GENDER_NEUTRALITY',
  'IDIOMS',
  'MISC',
  'NONSTANDARD_PHRASES',
  'PLAIN_ENGLISH',
  'PROPER_NOUNS',
  'REGIONALISMS',
] as const;

// ---------------------------------------------------------------------------
// GrammarInteractiveBox — draggable + resizable red bounding box for manually
// added grammar errors. Coordinates are percentages (0–100).
// ---------------------------------------------------------------------------
export function GrammarInteractiveBox({
  box,
  containerRef,
  onChange,
  onChangeEnd,
}: {
  box: { x: number; y: number; width: number; height: number };
  containerRef: React.RefObject<HTMLDivElement | null>;
  onChange: (box: { x: number; y: number; width: number; height: number }) => void;
  onChangeEnd: (box: { x: number; y: number; width: number; height: number }) => void;
}) {
  const startInteraction = (e: React.PointerEvent, action: string) => {
    e.preventDefault();
    e.stopPropagation();

    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startBox = { ...box };
    let latestBox = { ...box };

    const clamp = (b: typeof box) => {
      const width = Math.max(1, Math.min(b.width, 100));
      const height = Math.max(1, Math.min(b.height, 100));
      return {
        x: Math.max(0, Math.min(b.x, 100 - width)),
        y: Math.max(0, Math.min(b.y, 100 - height)),
        width,
        height,
      };
    };

    const handlePointerMove = (mv: PointerEvent) => {
      const dx = ((mv.clientX - startX) / rect.width) * 100;
      const dy = ((mv.clientY - startY) / rect.height) * 100;
      const nb = { ...startBox };
      if (action === 'move') {
        nb.x += dx;
        nb.y += dy;
      } else {
        if (action.includes('n')) { nb.y += dy; nb.height -= dy; }
        if (action.includes('s')) { nb.height += dy; }
        if (action.includes('w')) { nb.x += dx; nb.width -= dx; }
        if (action.includes('e')) { nb.width += dx; }
      }
      if (nb.width > 1 && nb.height > 1) {
        latestBox = clamp(nb);
        onChange(latestBox);
      }
    };

    const handlePointerUp = () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      onChangeEnd(latestBox);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handleClass =
    'absolute w-3 h-3 rounded-full z-30 bg-red-600 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity';

  return (
    <div
      className="absolute border-2 border-red-600 bg-red-500/20 cursor-move group z-25"
      style={{ left: `${box.x}%`, top: `${box.y}%`, width: `${box.width}%`, height: `${box.height}%` }}
      onPointerDown={e => startInteraction(e, 'move')}
    >
      <div className={cn(handleClass, '-top-1.5 -left-1.5 cursor-nwse-resize')} onPointerDown={e => startInteraction(e, 'nw')} />
      <div className={cn(handleClass, '-top-1.5 -right-1.5 cursor-nesw-resize')} onPointerDown={e => startInteraction(e, 'ne')} />
      <div className={cn(handleClass, '-bottom-1.5 -left-1.5 cursor-nesw-resize')} onPointerDown={e => startInteraction(e, 'sw')} />
      <div className={cn(handleClass, '-bottom-1.5 -right-1.5 cursor-nwse-resize')} onPointerDown={e => startInteraction(e, 'se')} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// GrammarOverlay — static red-underline overlays + optional interactive editing
// box for the currently-being-added manual error.
// ---------------------------------------------------------------------------
export function GrammarOverlay({
  evaluation,
  pageIndex,
  selectedErrorId,
  onSelect,
  editingBox,
  containerRef,
  onEditingBoxChange,
  onEditingBoxChangeEnd,
}: {
  evaluation: GrammarEvaluationView | undefined;
  pageIndex: number;
  selectedErrorId: string | null;
  onSelect: (id: string) => void;
  editingBox?: { x: number; y: number; width: number; height: number; pageNumber: number } | null;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  onEditingBoxChange?: (box: { x: number; y: number; width: number; height: number }) => void;
  onEditingBoxChangeEnd?: (box: { x: number; y: number; width: number; height: number }) => void;
}) {
  const hasErrors = evaluation?.errors?.length;
  const showEditingBox = editingBox != null && editingBox.pageNumber === pageIndex && containerRef;

  if (!hasErrors && !showEditingBox) return null;

  return (
    <>
      {evaluation?.errors
        ?.filter(err => err.box && err.box.pageNumber === pageIndex)
        .map(err => {
          const box = err.box!;
          const isSelected = selectedErrorId === err.id;
          return (
            <div
              key={err.id}
              onClick={e => {
                e.stopPropagation();
                onSelect(err.id);
              }}
              className={cn(
                'absolute cursor-pointer transition-colors',
                isSelected ? 'bg-red-500/20' : 'hover:bg-red-500/10',
              )}
              style={{
                left: `${box.x}%`,
                top: `${box.y}%`,
                width: `${box.width}%`,
                height: `${box.height}%`,
                borderBottom: '2px solid rgb(220 38 38)',
                zIndex: isSelected ? 20 : 10,
              }}
              title={err.shortMessage || err.message}
            />
          );
        })}

      {showEditingBox && onEditingBoxChange && onEditingBoxChangeEnd && (
        <GrammarInteractiveBox
          box={editingBox}
          containerRef={containerRef!}
          onChange={onEditingBoxChange}
          onChangeEnd={onEditingBoxChangeEnd}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// GrammarPanel — right-panel summary + error cards.
// ---------------------------------------------------------------------------
export function GrammarPanel({
  evaluation,
  selectedErrorId,
  onSelect,
  onDeleteError,
  onEditError,
  onSaveError,
  editingErrorId,
  editingCategory,
  editingMessage,
  onEditingCategoryChange,
  onEditingMessageChange,
}: {
  evaluation: GrammarEvaluationView | undefined;
  selectedErrorId: string | null;
  onSelect: (id: string) => void;
  onDeleteError?: (errorId: string) => void;
  onEditError?: (errorId: string) => void;
  onSaveError?: () => void;
  editingErrorId?: string | null;
  editingCategory?: string;
  editingMessage?: string;
  onEditingCategoryChange?: (cat: string) => void;
  onEditingMessageChange?: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (!selectedErrorId) return;
    cardRefs.current.get(selectedErrorId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedErrorId]);

  if (!evaluation) {
    return (
      <div className="bg-white dark:bg-card rounded-xl border border-slate-200 dark:border-slate-700 p-3 shadow-sm">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-semibold mb-2">
          <SpellCheck size={18} className="text-slate-400 dark:text-slate-500" />
          {t('Canvas.grading.grammarTitle')}
        </div>
        <p className="text-sm text-slate-400 dark:text-slate-500 italic">
          {t('Canvas.grading.noGrammarEvaluationForTask', { defaultValue: 'Keine Grammatikauswertung für diese Aufgabe.' })}
        </p>
      </div>
    );
  }

  const errors = evaluation.errors ?? [];

  return (
    <div className="flex flex-col gap-3">
      {/* Summary */}
      <div className="bg-white dark:bg-card rounded-xl border border-slate-200 dark:border-slate-700 p-3 shadow-sm">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 font-semibold mb-2">
          <SpellCheck size={18} className="text-red-600" />
          {t('Canvas.grading.grammarTitle')}
        </div>
        <div className="flex gap-4 text-sm">
          <div>
            <div className="text-2xl font-bold text-red-600">{evaluation.errorCount}</div>
            <div className="text-slate-500 dark:text-slate-400">{t('Canvas.grading.grammarErrors')}</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-700 dark:text-slate-300">
              {evaluation.fehlerindex.toFixed(1)}
            </div>
            <div className="text-slate-500 dark:text-slate-400">{t('Canvas.grading.fehlerindex')}</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-700 dark:text-slate-300">{evaluation.wordCount}</div>
            <div className="text-slate-500 dark:text-slate-400">{t('Canvas.grading.words')}</div>
          </div>
        </div>
      </div>

      {/* Error cards */}
      {errors.length === 0 ? (
        <div className="text-sm text-slate-500 dark:text-slate-400 text-center py-6">
          {t('Canvas.grading.noGrammarErrors')}
        </div>
      ) : (
        errors.map(err => (
          <GrammarErrorCard
            key={err.id}
            error={err}
            isSelected={selectedErrorId === err.id}
            isEditing={editingErrorId === err.id}
            editingCategory={editingErrorId === err.id ? editingCategory : undefined}
            editingMessage={editingErrorId === err.id ? editingMessage : undefined}
            onSelect={() => onSelect(err.id)}
            onEdit={onEditError && editingErrorId !== err.id ? () => onEditError(err.id) : undefined}
            onSave={editingErrorId === err.id ? onSaveError : undefined}
            onDelete={onDeleteError ? () => onDeleteError(err.id) : undefined}
            onCategoryChange={editingErrorId === err.id ? onEditingCategoryChange : undefined}
            onMessageChange={editingErrorId === err.id ? onEditingMessageChange : undefined}
            registerRef={(el: HTMLDivElement | null) => {
              if (el) cardRefs.current.set(err.id, el);
              else cardRefs.current.delete(err.id);
            }}
          />
        ))
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// GrammarErrorCard — single error card, with optional inline editing mode.
// ---------------------------------------------------------------------------
function GrammarErrorCard({
  error,
  isSelected,
  isEditing,
  editingCategory,
  editingMessage,
  onSelect,
  onEdit,
  onSave,
  onDelete,
  onCategoryChange,
  onMessageChange,
  registerRef,
}: {
  error: GrammarErrorView;
  isSelected: boolean;
  isEditing?: boolean;
  editingCategory?: string;
  editingMessage?: string;
  onSelect: () => void;
  onEdit?: () => void;
  onSave?: () => void;
  onDelete?: () => void;
  onCategoryChange?: (cat: string) => void;
  onMessageChange?: (msg: string) => void;
  registerRef: (el: HTMLDivElement | null) => void;
}) {
  const { t } = useTranslation();
  const rawCategory = isEditing ? (editingCategory ?? error.category) : (error.category || error.ruleId);
  const categoryLabel = rawCategory
    ? t(`Canvas.grading.errorCategories.${rawCategory}`, { defaultValue: rawCategory })
    : '';

  return (
    <div
      ref={registerRef}
      onClick={!isEditing ? onSelect : undefined}
      className={cn(
        'rounded-xl border p-3 shadow-sm bg-white dark:bg-card',
        isEditing
          ? 'border-red-400 ring-2 ring-red-100 dark:ring-red-900/30 cursor-default'
          : isSelected
            ? 'border-red-400 ring-2 ring-red-100 dark:ring-red-900/30 cursor-pointer'
            : 'border-slate-200 dark:border-slate-700 hover:border-red-300 cursor-pointer',
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        {isEditing && onCategoryChange ? (
          <select
            value={editingCategory ?? error.category}
            onChange={e => { e.stopPropagation(); onCategoryChange(e.target.value); }}
            onClick={e => e.stopPropagation()}
            className="text-xs font-semibold text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-red-400"
          >
            {GRAMMAR_CATEGORIES.map(cat => (
              <option key={cat} value={cat}>
                {t(`Canvas.grading.errorCategories.${cat}`, { defaultValue: cat })}
              </option>
            ))}
          </select>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-1.5 py-0.5">
            <AlertCircle size={12} />
            {categoryLabel}
          </span>
        )}
        <div className="flex items-center gap-1">
          {!error.box && !isEditing && (
            <span className="text-[10px] text-slate-400" title={t('Canvas.grading.notLocated')}>
              {t('Canvas.grading.notLocatedShort')}
            </span>
          )}
          {isEditing && onSave ? (
            <button
              onClick={e => { e.stopPropagation(); onSave(); }}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              title={t('Canvas.grading.saveGrammarError', { defaultValue: 'Speichern' })}
            >
              <Check size={13} />
            </button>
          ) : onEdit ? (
            <button
              onClick={e => { e.stopPropagation(); onEdit(); }}
              className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              title={t('Canvas.grading.editGrammarError', { defaultValue: 'Fehler bearbeiten' })}
            >
              <Pencil size={13} />
            </button>
          ) : null}
          {onDelete && (
            <button
              onClick={e => { e.stopPropagation(); onDelete(); }}
              className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title={t('Canvas.grading.deleteGrammarError', { defaultValue: 'Fehler entfernen' })}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>

      {isEditing && onMessageChange ? (
        <input
          type="text"
          value={editingMessage ?? error.message}
          placeholder={t('Canvas.grading.mistakePlaceholder', { defaultValue: 'Fehlerbeschreibung…' })}
          onChange={e => { e.stopPropagation(); onMessageChange(e.target.value); }}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onSave?.(); } }}
          autoFocus
          className="w-full text-sm text-slate-700 dark:text-slate-300 bg-transparent border-b border-slate-300 dark:border-slate-600 focus:outline-none focus:border-red-400 pb-0.5"
        />
      ) : (
        <div className="text-sm text-slate-700 dark:text-slate-300">{error.message || <span className="italic text-slate-400">{t('Canvas.grading.mistakePlaceholder', { defaultValue: 'Fehlerbeschreibung…' })}</span>}</div>
      )}
    </div>
  );
}
